import Editor from '@monaco-editor/react'
import { useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import Button from '@/components/Button'
import { Beautify } from '@/components/Icons'
import Label from '@/components/Label'
import { Row } from '@/components/StyledComponents'
import cn from '@/utilities/cn'

import messages from './messages'

const StyledRow = styled(Row)`
  justify-content: flex-start;
  margin-bottom: 5px;
`
const StyledFieldLabelRow = styled(StyledRow)`
  margin-top: 10px;
  justify-content: space-between;
`

const StyledButton = styled(Button)`
  padding: 0 0.5rem;
  font-weight: bold;
  display: flex;
  align-items: center;
  margin-left: -9px;
  min-height: 20px;
`

const StyledBeautifyIcon = styled(Beautify)`
  margin-left: 5px;
`

const ScriptOperationContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const CodeEditorContainer: any = styled.div`
  height: 500px;
  margin-top: 10px;
  display: block;
  border: 1px solid ${({ errors, name }: any) => (errors[name] ? 'var(--wg-red)' : 'transparent')};
  .monaco-editor {
    .margin {
    }
  }
`

const CodeEditor = ({
  label = '',
  name = 'triggerData.payload',
  required = false,
  description,
  readOnly = false,
  defaultValue,
  setFieldValue,
  setFieldError,
  errors,
  schemas,
  editorErrorAsWarning
}: any) => {
  const intl = useIntl()

  const monacoRef = useRef<any>(null)
  const [validationError, setValidationError] = useState('')

  const formatDocument = async () => await monacoRef.current.getAction('editor.action.formatDocument').run()

  const handleBeautify = () => formatDocument()

  const editorOnMount = (editor: any, monaco: any) => {
    monacoRef.current = editor
    monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
      validate: true,
      schemas
    })
    setTimeout(async () => {
      await formatDocument()
      if (readOnly) editor.updateOptions({ readOnly: true })
    }, 50)
  }

  // Update field in Formik
  const onEditorChange = (value: any) => {
    let payload = value
    try {
      payload = JSON.parse(value)
    } catch {}

    // an object is expected as payload, but failing that a string will allow us to throw the appropriate error
    setFieldValue(name, payload)
  }

  const onEditorValidate = (markers: { message: string }[]) => {
    let errorMessage = ''
    markers.forEach((marker: { message: string }) => {
      errorMessage += ` ${marker.message}`
    })
    setFieldError(name, errorMessage)
    setValidationError(errorMessage)
  }

  return (
    <>
      <StyledFieldLabelRow>
        <Label required={required}>{label}</Label>
        <StyledButton color={'transparent'} onClick={handleBeautify} size={'small'}>
          {intl.formatMessage(messages.beautify)}
          <StyledBeautifyIcon />
        </StyledButton>
      </StyledFieldLabelRow>
      {description && <div className="text-sm">{description}</div>}
      <ScriptOperationContainer>
        <CodeEditorContainer readOnly={readOnly} errors={errors} name={name}>
          <Editor
            onMount={editorOnMount}
            theme="light"
            options={{
              automaticLayout: true,
              lineNumbersMinChars: 2,
              scrollBeyondLastLine: false,
              lineDecorationsWidth: 0,
              minimap: {
                enabled: false
              },
              scrollbar: {
                useShadows: false,
                alwaysConsumeMouseWheel: false
              },
              autoIndent: 'full',
              formatOnPaste: true,
              formatOnType: true
            }}
            defaultLanguage="json"
            defaultValue={defaultValue}
            onChange={onEditorChange}
            onValidate={onEditorValidate}
          />
        </CodeEditorContainer>
      </ScriptOperationContainer>
      {validationError && (
        <div className={cn('mt-2 text-sm', editorErrorAsWarning ? 'text-yellow-700' : 'text-red-600')}>
          {validationError}
        </div>
      )}
    </>
  )
}

export default CodeEditor
